import type { AccessCode, AccessCodeMetadata } from '@gat-ship/types'

/**
 * Functional core — per-permission-item decisions.
 *
 * Pure deciders consuming an `AccessCodeMetadata` entry (read-only) plus the
 * role's currently-granted permission codes. They answer "is this item
 * granted / hidden / disabled / visible / a search match?" as plain values —
 * no `ref`, no `computed`, no IO. The `accessCodeMetadata` table itself stays
 * in `access-control-text.ts`; the shell composable injects the resolved entry.
 */

/** Whether all the permissions this item grants are present on the role. */
export function isPermissionGranted(
  metadata: AccessCodeMetadata,
  permissionCodes: readonly string[]
): boolean {
  return metadata.addedWhenPermissionsAreGranted.every((code) =>
    permissionCodes.includes(code)
  )
}

/** The required permissions for this item that the role does not yet hold. */
export function requiredPermissionsNotGranted(
  metadata: AccessCodeMetadata,
  permissionCodes: readonly string[]
): AccessCode[] {
  return (metadata.requiresPermissions ?? []).filter(
    (code) => !permissionCodes.includes(code)
  )
}

/** Whether this item declares any prerequisite permissions. */
export function permissionRequiresRoles(metadata: AccessCodeMetadata): boolean {
  return (metadata.requiresPermissions ?? []).length > 0
}

/**
 * Whether the item is hidden under the "hide required permissions" preference:
 * hidden only when the preference is on, the item has prerequisites, and at
 * least one prerequisite is missing.
 */
export function isPermissionHidden(
  metadata: AccessCodeMetadata,
  permissionCodes: readonly string[],
  hideRequiredPermissions: boolean
): boolean {
  if (hideRequiredPermissions && permissionRequiresRoles(metadata)) {
    return requiredPermissionsNotGranted(metadata, permissionCodes).length > 0
  }
  return false
}

/** Whether the toggle is disabled because a prerequisite permission is missing. */
export function isPermissionDisabled(
  metadata: AccessCodeMetadata,
  permissionCodes: readonly string[]
): boolean {
  const requires = metadata.requiresPermissions ?? []
  if (requires.length > 0) {
    return !requires.every((code) => permissionCodes.includes(code))
  }
  return false
}

/** Whether the item matches the (case-insensitive) search term. */
export function permissionMatchesSearch(
  metadata: AccessCodeMetadata,
  context: { title: string; groupName: string },
  searchValue: string
): boolean {
  if (!searchValue) {
    return true
  }
  const searchWords = [
    context.title,
    ...(metadata.searchWords ?? []),
    metadata.description,
    context.groupName
  ]
  return searchWords.some(
    (word) => word?.toLowerCase().includes(searchValue.toLowerCase()) ?? false
  )
}

/** Whether the item should render given the current search + hide preferences. */
export function permissionItemIsVisible(
  metadata: AccessCodeMetadata,
  permissionCodes: readonly string[],
  options: {
    searchValue: string | undefined
    hideRequiredPermissions: boolean
    title: string
    groupName: string
  }
): boolean {
  if (
    metadata.hiddenWhenPermissionsAreGranted?.every((code) =>
      permissionCodes.includes(code)
    )
  ) {
    return false
  }
  const { searchValue, hideRequiredPermissions, title, groupName } = options
  if (!searchValue || typeof searchValue !== 'string') {
    return !isPermissionHidden(
      metadata,
      permissionCodes,
      hideRequiredPermissions
    )
  }
  if (searchValue.length > 0) {
    return permissionMatchesSearch(metadata, { title, groupName }, searchValue)
  }
  return !isPermissionHidden(metadata, permissionCodes, hideRequiredPermissions)
}

/**
 * The "Requires …" hint for unmet prerequisites. Returns `undefined` when all
 * prerequisites are granted, a single-line hint for one, and a count + tooltip
 * listing each title for several. `titleOf` resolves a code to its title.
 */
export function rolesRequiredText(
  metadata: AccessCodeMetadata,
  permissionCodes: readonly string[],
  titleOf: (code: AccessCode) => string
): { displayText: string; tooltipText?: string } | undefined {
  const notGranted = requiredPermissionsNotGranted(metadata, permissionCodes)
  if (notGranted.length === 0) {
    return undefined
  }
  if (notGranted.length === 1) {
    const [only] = notGranted
    return { displayText: `Requires ${titleOf(only!)}` }
  }
  return {
    displayText: `Requires ${notGranted.length} Permissions`,
    tooltipText: notGranted.map((code) => titleOf(code)).join('\r\n')
  }
}
